import { MouseEvent } from 'react';
import { UseEmblaCarouselType } from 'embla-carousel-react';

import { useDispensary } from 'src/hooks/use-dispensary';
import { useCarousel } from 'src/hooks/use-carousel';
import { categoryStockPhotos } from '../constants';
import { Category } from '../categories.types';
import { LIBRARY_PER_PAGE } from './constants';

type UseSelectImageModalProps = {
  category: Category;
  selectedImage: string;
};

type UseSelectImageModalReturn = {
  photos: string[];
  carouselRef: UseEmblaCarouselType[0];
  isPrevBtnEnabled: boolean;
  isNextBtnEnabled: boolean;
  scrollPrev: (event: MouseEvent) => void;
  scrollNext: (event: MouseEvent) => void;
};

export const useSelectImageModal = ({
  category,
  selectedImage,
}: UseSelectImageModalProps): UseSelectImageModalReturn => {
  const { category: value } = category;
  const dispensary = useDispensary();
  const customCategoryPhotos = dispensary.customCategoryPhotos ?? [];
  const uploadedPhotos = customCategoryPhotos
    .filter(
      (customCategoryPhoto) =>
        customCategoryPhoto?.url && customCategoryPhoto.category === value && customCategoryPhoto.collectionId
    )
    .map((customCategoryPhoto) => customCategoryPhoto?.url ?? '');

  const customCategoryUploadedPhotos = customCategoryPhotos
    .filter(
      (customCategoryPhoto) => customCategoryPhoto?.url && customCategoryPhoto.collectionId === category.collectionId
    )
    .map((customCategoryPhoto) => customCategoryPhoto?.url ?? '');

  const getPhotos = (): string[] => {
    if (category.collectionId) {
      // For outlet collections, always include cardImage if it exists
      if (category.isOutlet && category.cardImage) {
        // Filter out cardImage from uploaded photos to avoid duplicates
        const otherUploadedPhotos = customCategoryUploadedPhotos.filter((url) => url !== category.cardImage);
        return [category.cardImage, ...otherUploadedPhotos];
      }

      // For non-outlet custom categories, include original image plus any uploaded photos
      const otherUploadedPhotos = customCategoryUploadedPhotos.filter((url) => url !== category.image);
      return [category.image, ...otherUploadedPhotos];
    }

    return [...categoryStockPhotos[category.category], ...uploadedPhotos];
  };

  const photos = getPhotos();
  const index = photos.indexOf(selectedImage);
  const startIndex = index < 0 ? 0 : Math.floor(index / LIBRARY_PER_PAGE);

  const { carouselRef, prevBtnEnabled, nextBtnEnabled, scrollPrev, scrollNext } = useCarousel({
    carouselOptions: {
      startIndex,
    },
  });

  return {
    photos,
    carouselRef,
    isPrevBtnEnabled: prevBtnEnabled,
    isNextBtnEnabled: nextBtnEnabled,
    scrollPrev,
    scrollNext,
  };
};
